export interface TextHighlightConfig {
  /** The root element or text layer node containing the spans */
  container: HTMLElement;
  /** Flat array of string terms to find and highlight */
  keywords: string[];
  /** Optional class name to attach to the generated <mark> tags */
  className?: string;
}

/** Keywords shorter than this (after trimming) are ignored to avoid noisy highlights */
const MIN_KEYWORD_LENGTH = 2;

/**
 * Sweeps a DOM container, aggregates fractured multi-node text,
 * and wraps whole-word keyword matches in <mark> elements.
 */
export function highlightPDFText(config: TextHighlightConfig): void {
  const { container, keywords, className = 'custom-bold-highlight' } = config;

  if (!container || !keywords || keywords.length === 0) return;

  // 1. Gather, clean and dedupe keywords; longest first so "John Smith" wins over "John"
  const cleanedKeywords = Array.from(new Set(
    keywords
      .filter(val => val !== null && val !== undefined)
      .map(val => String(val).trim())
      .filter(val => val.length >= MIN_KEYWORD_LENGTH && val !== 'null' && val !== 'undefined')
  )).sort((a, b) => b.length - a.length);

  if (cleanedKeywords.length === 0) return;

  // 2. Escape regex characters (unicode-mode safe) and let whitespace match line/span gaps
  const escapedValues = cleanedKeywords.map(val =>
    val
      .replace(/[.*+?^${}()|[\]\\\/]/g, '\\$&')
      .replace(/\s+/g, '\\s*')
  );
  // Whole-word match: keyword must not be preceded/followed by a letter or digit
  const combinedRegex = new RegExp(
    `(?<![\\p{L}\\p{N}])(?:${escapedValues.join('|')})(?![\\p{L}\\p{N}])`,
    'giu'
  );

  // 3. Target the text layer container explicitly
  const textLayer = container.classList.contains('textLayer')
    ? container
    : container.querySelector('.textLayer');

  if (!textLayer) return;

  // 4. Extract text nodes and track offsets; <br> line breaks become '\n' separators
  const textNodes: Text[] = [];
  const nodeOffsets: number[] = [];
  let fullText = '';

  const walk = document.createTreeWalker(
    textLayer,
    NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT,
    null
  );
  let currentNode = walk.nextNode();

  while (currentNode) {
    if (currentNode.nodeType === Node.ELEMENT_NODE) {
      if ((currentNode as Element).tagName === 'BR') fullText += '\n';
    } else {
      const parent = currentNode.parentElement;
      const alreadyHighlighted = parent && (parent.tagName === 'MARK' || parent.classList.contains(className));
      if (alreadyHighlighted) {
        // Keep offsets consistent but never re-wrap existing highlights
        fullText += '\n';
      } else {
        textNodes.push(currentNode as Text);
        nodeOffsets.push(fullText.length);
        fullText += currentNode.nodeValue || '';
      }
    }
    currentNode = walk.nextNode();
  }

  // 5. Find matches in the unified text string
  let match: RegExpExecArray | null;
  const matchesToHighlight: { start: number; end: number }[] = [];

  while ((match = combinedRegex.exec(fullText)) !== null) {
    if (match[0].length === 0) {
      combinedRegex.lastIndex++;
      continue;
    }
    matchesToHighlight.push({ start: match.index, end: match.index + match[0].length });
  }

  // 6. Process matches in reverse order. splitText keeps the original node as the prefix,
  //    so offsets for earlier matches stay valid even after later ones are wrapped.
  for (let i = matchesToHighlight.length - 1; i >= 0; i--) {
    const { start, end } = matchesToHighlight[i];
    executeHighlightRange(textNodes, nodeOffsets, start, end, className);
  }
}

/** Wraps the [start, end) range of the unified text across one or more text nodes */
function executeHighlightRange(
  textNodes: Text[],
  nodeOffsets: number[],
  start: number,
  end: number,
  className: string
): void {
  for (let i = 0; i < textNodes.length; i++) {
    const node = textNodes[i];
    const nodeStart = nodeOffsets[i];
    const nodeEnd = nodeStart + (node.nodeValue || '').length;

    if (nodeStart >= end) break;
    if (nodeEnd <= start || !node.parentNode) continue;

    const localStart = Math.max(start, nodeStart) - nodeStart;
    const localEnd = Math.min(end, nodeEnd) - nodeStart;
    wrapTextSegment(node, localStart, localEnd, className);
  }
}

/** Isolates [localStart, localEnd) of a text node and wraps it in a <mark> */
function wrapTextSegment(node: Text, localStart: number, localEnd: number, className: string): void {
  const length = (node.nodeValue || '').length;
  if (localStart >= localEnd) return;

  // Skip segments that are only whitespace (e.g. gap between words across spans)
  if (!(node.nodeValue || '').substring(localStart, localEnd).trim()) return;

  if (localEnd < length) node.splitText(localEnd);
  const target = localStart > 0 ? node.splitText(localStart) : node;

  const mark = document.createElement('mark');
  mark.className = className;
  target.parentNode!.insertBefore(mark, target);
  mark.appendChild(target);
}
